/**
 * 全局播放状态管理 Store
 * 管理播放器状态：当前歌曲、播放状态、播放时间等
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export const usePlaybackStore = defineStore('playback', () => {
  // ========== 状态 ==========
  
  // 当前播放的歌曲
  const currentSong = ref(null);
  
  // 播放状态
  const isPlaying = ref(false);
  
  // 播放时间
  const currentTime = ref(0);
  const duration = ref(0);
  let lastCurrentTimeWriteAt = 0;
  let lastCurrentTimeWriteValue = 0;
  
  // 播放模式：'list' 列表循环, 'single' 单曲循环, 'random' 随机播放
  const playMode = ref('list');
  // 仅网格直开全屏时生效的单曲循环，不改全局播放模式
  const gridFullscreenSingleRepeat = ref(false);
  
  // 播放列表
  const playlist = ref([]);
  
  // 错误信息
  const error = ref(null);
  
  // 当前播放策略类型：'audio' | 'video' | 'lyrics'
  const currentStrategyType = ref(null);
  
  // ========== 计算属性 ==========
  
  /**
   * 播放进度百分比 (0-100)
   */
  const progress = computed(() => {
    if (duration.value === 0) return 0;
    return (currentTime.value / duration.value) * 100;
  });
  
  /**
   * 是否有当前播放歌曲
   */
  const hasCurrentSong = computed(() => currentSong.value !== null);
  
  /**
   * 当前歌曲在播放列表中的索引
   */
  const currentSongIndex = computed(() => {
    if (!currentSong.value) return -1;
    const currentId = currentSong.value.id;
    return playlist.value.findIndex(song => {
      if (!song || song.id == null || currentId == null) return false;
      return String(song.id) === String(currentId);
    });
  });
  
  // ========== 操作方法 ==========
  
  /**
   * 设置当前播放歌曲
   * @param {Object|null} song - 歌曲对象
   */
  function setCurrentSong(song) {
    currentSong.value = song;
    currentTime.value = 0;
    lastCurrentTimeWriteValue = 0;
    lastCurrentTimeWriteAt = 0;
    error.value = null;
    console.log('🎵 [PlaybackStore] 设置当前歌曲:', song?.title || 'null');
  }
  
  /**
   * 设置播放状态
   * @param {boolean} playing - 是否播放中
   */
  function setPlaying(playing) {
    isPlaying.value = playing;
    console.log('🎵 [PlaybackStore] 设置播放状态:', playing);
  }
  
  /**
   * 设置当前播放时间
   * @param {number} time - 播放时间（秒）
   * @param {{ force?: boolean }} [options]
   */
  function setCurrentTime(time, options = {}) {
    if (!Number.isFinite(time) || isNaN(time)) {
      return;
    }

    const { force = false } = options;
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const delta = Math.abs(time - lastCurrentTimeWriteValue);
    const elapsed = now - lastCurrentTimeWriteAt;

    // 常规播放进度：最多约 4 次/秒写入，避免 timeupdate 触发整页重渲染
    if (!force && delta < 0.5 && elapsed < 250 && Math.abs(time - currentTime.value) < 0.2) {
      return;
    }

    lastCurrentTimeWriteAt = now;
    lastCurrentTimeWriteValue = time;
    currentTime.value = time;
  }
  
  /**
   * 设置总时长
   * @param {number} dur - 总时长（秒）
   */
  function setDuration(dur) {
    if (!Number.isFinite(dur) || isNaN(dur) || dur <= 0) {
      return;
    }
    if (Math.abs(duration.value - dur) < 0.01) {
      return;
    }
    duration.value = dur;
  }
  
  /**
   * 设置播放模式
   * @param {'list'|'single'|'random'} mode - 播放模式
   */
  function setPlayMode(mode) {
    const oldMode = playMode.value;
    playMode.value = mode;
    console.log('🎵 [PlaybackStore] 设置播放模式:', oldMode, '->', mode);
    
    // 切换播放模式时，可以通过事件通知策略管理器清空随机播放历史
    // 这里我们通过自定义事件来处理
    if (mode !== 'random' || oldMode !== 'random') {
      window.dispatchEvent(new CustomEvent('playback-mode-changed', { detail: { mode } }));
    }
  }

  function setGridFullscreenSingleRepeat(active) {
    gridFullscreenSingleRepeat.value = !!active;
  }
  
  /**
   * 设置播放列表
   * @param {Array} songs - 歌曲列表
   */
  function setPlaylist(songs) {
    playlist.value = songs || [];
    console.log('🎵 [PlaybackStore] 设置播放列表，歌曲数量:', playlist.value.length);
  }
  
  /**
   * 设置错误信息
   * @param {Error|null} err - 错误对象
   */
  function setError(err) {
    error.value = err;
    if (err) {
      console.error('🎵 [PlaybackStore] 播放错误:', err);
    }
  }
  
  /**
   * 设置当前播放策略类型
   * @param {'audio'|'video'|'lyrics'|null} type - 策略类型
   */
  function setCurrentStrategyType(type) {
    currentStrategyType.value = type;
    console.log('🎵 [PlaybackStore] 设置策略类型:', type);
  }
  
  /**
   * 重置播放状态
   */
  function reset() {
    currentSong.value = null;
    isPlaying.value = false;
    currentTime.value = 0;
    duration.value = 0;
    lastCurrentTimeWriteValue = 0;
    lastCurrentTimeWriteAt = 0;
    error.value = null;
    currentStrategyType.value = null;
    console.log('🎵 [PlaybackStore] 重置播放状态');
  }
  
  return {
    // 状态
    currentSong,
    isPlaying,
    currentTime,
    duration,
    playMode,
    gridFullscreenSingleRepeat,
    playlist,
    error,
    currentStrategyType,
    
    // 计算属性
    progress,
    hasCurrentSong,
    currentSongIndex,
    
    // 方法
    setCurrentSong,
    setPlaying,
    setCurrentTime,
    setDuration,
    setPlayMode,
    setGridFullscreenSingleRepeat,
    setPlaylist,
    setError,
    setCurrentStrategyType,
    reset
  };
});

